import { Injectable, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import {
  ConnectWebSocket,
  WebSocketConnected,
  WebSocketDisconnected,
  WebSocketMessageReceived,
  SendWebSocketMessage,
} from './websocket.actions';
import {
  GuestLogin,
  GuestLoginSuccess,
  PlayerJoined,
  PlayerLeft,
  PlayerListUpdated,
  LobbyChatReceived,
  SendLobbyChat,
  ChatHistoryLoaded,
  AuthLogin,
  AuthLoginSuccess,
  KeycloakLogin,
  KeycloakRegister,
  AvailableGamesReceived,
  SendChallenge,
  ChallengeSentConfirmed,
  ChallengeReceived,
  AcceptChallenge,
  DeclineChallenge,
  RevokeChallenge,
  ChallengeDeclined,
  ChallengeRevoked,
  ChallengeAccepted,
  MessageLiked,
  LikeMessage,
  ActiveGamesListReceived,
  ActiveGameAdded,
  ActiveGameRemoved,
  ActiveGameInfo,
  ObserveGame,
  StopObserving,
  SendGameChat,
  BlockUser,
  UnblockUser,
  ListBlockedUsers,
  BlockedUsersListReceived,
  StartDemonstration,
  StartReview,
  DemonstrationStarted,
  ListStoredGames,
  StoredGamesListReceived,
  ObserveStoredGame,
  StoredGameInfo,
  FriendsListReceived,
  FriendRequestsListReceived,
  FriendRequestReceivedNotification,
  FriendRequestSentNotification,
  FriendRequestAcceptedNotification,
  FriendRequestDeclinedNotification,
  FriendRemovedNotification,
  FriendOnlineNotification,
  FriendOfflineNotification,
  SendFriendRequest,
  AcceptFriendRequest,
  DeclineFriendRequest,
  CancelFriendRequest,
  RemoveFriend,
  ListFriends,
  ListFriendRequests,
  GetProfile,
  SetPlayerListTab,
  ProfileReceived,
  ClearProfile,
  UpdateProfile,
  UpdateNotifyFriendOnline,
  FriendInfo,
  RequestInfo,
  ProfileInfo,
  VariantStats,
} from './lobby.actions';
import {
  GameStarted,
  GameStateUpdated,
  GameOver,
  InvalidMoveReceived,
  MovesHistoryReceived,
  ObservationStarted,
  ObserverJoined,
  ObserverLeft,
  GameChatReceived,
  DrawOfferDeclinedReceived,
} from './game.actions';
import { MoveEntry, GameChatEntry } from './game.state';
import { GameState } from './game.state';
import { AuthService } from '../services/auth.service';

export interface ChatMessage {
  messageId: string;
  username: string;
  message: string;
  timestamp: string;
  kind: 'chat' | 'logon' | 'logoff' | 'system';
  likes: number;
}

export interface IncomingChallenge {
  challengeId: string;
  fromUsername: string;
  game: string;
  initialTimeSecs: number;
  incrementSecs: number;
  rated: boolean;
  isPublic: boolean;
}

export interface OutgoingChallenge {
  challengeId: string;
  toUsername: string;
  game: string;
  initialTimeSecs: number;
  incrementSecs: number;
  rated: boolean;
  isPublic: boolean;
}

export interface PlayerInfo {
  username: string;
  is_bot: boolean;
}

export interface ActiveGameEntry {
  gameId: string;
  game: string;
  players: { seat: number; username: string }[];
  initialTimeSecs: number;
  incrementSecs: number;
  startedAt: string;
  public: boolean;
  observerCount: number;
}

export interface LobbyStateModel {
  currentUser: { clientId: number; username: string; displayName?: string } | null;
  pendingLogin: string | null;
  pendingAuthToken: string | null;
  isGuest: boolean;
  players: PlayerInfo[];
  chatMessages: ChatMessage[];
  availableGames: string[];
  incomingChallenges: IncomingChallenge[];
  outgoingChallenges: OutgoingChallenge[];
  activeGames: ActiveGameEntry[];
  blockedUsernames: string[];
  storedGames: StoredGameInfo[];
  storedGamesOffset: number;
  friends: FriendInfo[];
  incomingFriendRequests: RequestInfo[];
  outgoingFriendRequests: RequestInfo[];
  playerListTab: 'online' | 'friends' | 'requests' | 'blocked';
  notifyFriendOnline: boolean;
  profile: ProfileInfo | null;
  profileLoading: boolean;
  friendOnlineBuffer: string[];
}

@State<LobbyStateModel>({
  name: 'lobby',
  defaults: {
    currentUser: null,
    pendingLogin: null,
    pendingAuthToken: null,
    isGuest: true,
    players: [],
    chatMessages: [],
    availableGames: [],
    incomingChallenges: [],
    outgoingChallenges: [],
    activeGames: [],
    blockedUsernames: [],
    storedGames: [],
    storedGamesOffset: 0,
    friends: [],
    incomingFriendRequests: [],
    outgoingFriendRequests: [],
    playerListTab: 'online',
    notifyFriendOnline: true,
    profile: null,
    profileLoading: false,
    friendOnlineBuffer: [],
  },
})
@Injectable()
export class LobbyState {
  private router = inject(Router);
  private store = inject(Store);
  private authService = inject(AuthService);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private zone = inject(NgZone);
  private friendOnlineTimer: ReturnType<typeof setTimeout> | null = null;

  private normalizePlayerSeats(players: unknown): { seat: number; username: string }[] {
    if (!Array.isArray(players)) return [];
    return players.map((player, index) => {
      const p = player as Record<string, unknown>;
      const username = p['username'] ?? p['player_username'] ?? p['name'];
      const seat = typeof p['seat'] === 'number' ? p['seat'] : index;
      return {
        seat,
        username: typeof username === 'string' ? username : '',
      };
    });
  }

  private normalizeClockMs(clocks: unknown, initialTimeSecs: unknown, playerCount: number): number[] {
    const fallback = Number.isFinite(initialTimeSecs) ? (initialTimeSecs as number) * 1000 : 0;
    const source = Array.isArray(clocks) ? clocks : [];
    return Array.from({ length: playerCount }, (_, index) => {
      const value = source[index];
      return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
    });
  }

  @Selector()
  static currentUser(state: LobbyStateModel) {
    return state.currentUser;
  }

  @Selector()
  static players(state: LobbyStateModel) {
    return state.players;
  }

  @Selector()
  static chatMessages(state: LobbyStateModel) {
    return state.chatMessages;
  }

  @Selector()
  static playerCount(state: LobbyStateModel) {
    return state.players.length;
  }

  @Selector()
  static isLoggedIn(state: LobbyStateModel) {
    return state.currentUser !== null;
  }

  @Selector()
  static isGuest(state: LobbyStateModel) {
    return state.isGuest;
  }

  @Selector()
  static availableGames(state: LobbyStateModel) {
    return state.availableGames;
  }

  @Selector()
  static incomingChallenges(state: LobbyStateModel) {
    return state.incomingChallenges;
  }

  @Selector()
  static outgoingChallenges(state: LobbyStateModel) {
    return state.outgoingChallenges;
  }

  @Selector()
  static challengeCount(state: LobbyStateModel) {
    return state.incomingChallenges.length + state.outgoingChallenges.length;
  }

  @Selector()
  static activeGames(state: LobbyStateModel) {
    return state.activeGames;
  }

  @Selector()
  static storedGames(state: LobbyStateModel) {
    return state.storedGames;
  }

  @Selector()
  static blockedUsernames(state: LobbyStateModel) {
    return state.blockedUsernames;
  }

  @Selector()
  static friends(state: LobbyStateModel) {
    return state.friends;
  }

  @Selector()
  static onlineFriends(state: LobbyStateModel) {
    return state.friends.filter(f => f.online);
  }

  @Selector()
  static incomingFriendRequests(state: LobbyStateModel) {
    return state.incomingFriendRequests;
  }

  @Selector()
  static outgoingFriendRequests(state: LobbyStateModel) {
    return state.outgoingFriendRequests;
  }

  @Selector()
  static playerListTab(state: LobbyStateModel) {
    return state.playerListTab;
  }

  @Selector()
  static notifyFriendOnline(state: LobbyStateModel) {
    return state.notifyFriendOnline;
  }

  @Selector()
  static profile(state: LobbyStateModel) {
    return state.profile;
  }

  @Selector()
  static profileLoading(state: LobbyStateModel) {
    return state.profileLoading;
  }

  @Action(GuestLogin)
  guestLogin(ctx: StateContext<LobbyStateModel>, _action: GuestLogin) {
    ctx.patchState({ pendingLogin: '' });
    ctx.dispatch(new ConnectWebSocket());
  }

  @Action(WebSocketConnected)
  onConnected(ctx: StateContext<LobbyStateModel>) {
    const state = ctx.getState();
    if (state.pendingAuthToken) {
      ctx.dispatch(
        new SendWebSocketMessage({
          type: 'auth_login',
          payload: { token: state.pendingAuthToken },
        })
      );
      ctx.patchState({ pendingAuthToken: null });
    } else if (state.pendingLogin !== null) {
      ctx.dispatch(
        new SendWebSocketMessage({
          type: 'guest_login',
          payload: { session_token: null },
        })
      );
    }
  }

  @Action(WebSocketDisconnected)
  onDisconnected(ctx: StateContext<LobbyStateModel>) {
    ctx.patchState({
      currentUser: null,
      pendingLogin: null,
      pendingAuthToken: null,
      isGuest: true,
      players: [],
      chatMessages: [],
      availableGames: [],
      incomingChallenges: [],
      outgoingChallenges: [],
      friends: [],
      incomingFriendRequests: [],
      outgoingFriendRequests: [],
      blockedUsernames: [],
      profile: null,
      profileLoading: false,
      friendOnlineBuffer: [],
    });
    if (this.friendOnlineTimer) {
      clearTimeout(this.friendOnlineTimer);
      this.friendOnlineTimer = null;
    }
    ctx.dispatch(new GuestLogin());
  }

  @Action(WebSocketMessageReceived)
  onMessage(ctx: StateContext<LobbyStateModel>, action: WebSocketMessageReceived) {
    const { type, payload } = action.payload as { type: string; payload: Record<string, unknown> };

    switch (type) {
      case 'welcome':
        ctx.dispatch(
          new GuestLoginSuccess(
            payload['client_id'] as number,
            payload['username'] as string,
          )
        );
        break;
      case 'player_joined':
        ctx.dispatch(new PlayerJoined(
          payload['player'] as { username: string; is_bot: boolean }
        ));
        break;
      case 'player_left':
        ctx.dispatch(new PlayerLeft(payload['username'] as string));
        break;
      case 'player_list':
        ctx.dispatch(new PlayerListUpdated(payload['players'] as PlayerInfo[]));
        break;
      case 'lobby_chat':
        ctx.dispatch(
          new LobbyChatReceived(
            payload['message_id'] as string,
            payload['username'] as string,
            payload['message'] as string,
            payload['kind'] as 'chat' | 'logon' | 'logoff',
            payload['timestamp'] as string,
          )
        );
        break;
      case 'chat_history':
        ctx.dispatch(
          new ChatHistoryLoaded(
            payload['messages'] as Array<{
              message_id: string;
              username: string;
              message: string;
              timestamp: string;
              likes: string[];
  kind: 'chat' | 'logon' | 'logoff' | 'system';
            }>,
          )
        );
        break;
      case 'auth_success':
        ctx.dispatch(
          new AuthLoginSuccess(
            payload['client_id'] as number,
            payload['username'] as string,
            payload['display_name'] as string,
          )
        );
        break;
      case 'available_games':
        ctx.dispatch(new AvailableGamesReceived(payload['games'] as string[]));
        break;
      case 'challenge_sent':
        ctx.dispatch(
          new ChallengeSentConfirmed(
            payload['challenge_id'] as string,
            payload['to_username'] as string,
            payload['game'] as string,
            payload['initial_time_secs'] as number,
            payload['increment_secs'] as number,
            payload['rated'] as boolean,
            payload['public'] as boolean,
          )
        );
        break;
      case 'challenge_received':
        ctx.dispatch(
          new ChallengeReceived(
            payload['challenge_id'] as string,
            payload['from_username'] as string,
            payload['game'] as string,
            payload['initial_time_secs'] as number,
            payload['increment_secs'] as number,
            payload['rated'] as boolean,
            payload['public'] as boolean,
          )
        );
        break;
      case 'challenge_declined':
        ctx.dispatch(new ChallengeDeclined(payload['challenge_id'] as string));
        break;
      case 'challenge_revoked':
        ctx.dispatch(new ChallengeRevoked(payload['challenge_id'] as string));
        break;
      case 'challenge_accepted':
        ctx.dispatch(
          new ChallengeAccepted(
            payload['challenge_id'] as string,
            payload['game'] as string,
            payload['opponent'] as string,
          )
        );
        break;
      case 'game_started':
        ctx.dispatch(
          new GameStarted(
            payload['game_id'] as string,
            payload['game'] as string,
            this.normalizePlayerSeats(payload['players']),
            payload['your_seat'] as number | null ?? null,
            payload['initial_state'],
            payload['initial_time_secs'] as number,
            payload['increment_secs'] as number,
            payload['mode'] as string,
            payload['rated'] as boolean,
          )
        );
        break;
      case 'demonstration_started':
        ctx.dispatch(
          new DemonstrationStarted(
            payload['game_id'] as string,
            payload['game'] as string,
            this.normalizePlayerSeats(payload['players']),
          )
        );
        break;
      case 'game_state_update':
        ctx.dispatch(
          new GameStateUpdated(
            payload['game_id'] as string,
            payload['state'],
            payload['clocks_remaining_ms'] as number[],
            payload['current_seat'] as number | null ?? null,
          )
        );
        break;
      case 'game_over':
        ctx.dispatch(new GameOver(payload['game_id'] as string, payload['result'] as string));
        break;
      case 'invalid_move':
        ctx.dispatch(
          new InvalidMoveReceived(payload['game_id'] as string, payload['reason'] as string)
        );
        break;
      case 'message_liked':
        ctx.dispatch(
          new MessageLiked(
            payload['message_id'] as string,
            payload['like_count'] as number,
          )
        );
        break;
      case 'moves_history':
        ctx.dispatch(
          new MovesHistoryReceived(
            payload['game_id'] as string,
            payload['moves'] as MoveEntry[],
          )
        );
        break;
      case 'active_games_list':
        ctx.dispatch(
          new ActiveGamesListReceived(
            payload['games'] as ActiveGameInfo[],
          )
        );
        break;
      case 'active_game_added':
        ctx.dispatch(
          new ActiveGameAdded(
            payload['game'] as ActiveGameInfo,
          )
        );
        break;
      case 'active_game_removed':
        ctx.dispatch(
          new ActiveGameRemoved(
            payload['game_id'] as string,
          )
        );
        break;
      case 'observation_started':
        {
          const players = this.normalizePlayerSeats(payload['players']);
          ctx.dispatch(
            new ObservationStarted(
              payload['game_id'] as string,
              payload['game'] as string,
              players,
              payload['initial_state'],
              payload['initial_time_secs'] as number,
              payload['increment_secs'] as number,
              payload['current_state'],
              this.normalizeClockMs(payload['clocks_remaining_ms'], payload['initial_time_secs'], players.length),
              payload['current_seat'] as number | null ?? null,
              payload['moves'] as MoveEntry[],
              payload['current_ply'] as number,
              payload['rated'] as boolean,
              payload['observers'] as { username: string; is_bot: boolean }[],
              payload['chat'] as GameChatEntry[],
              payload['mode'] as string,
            )
          );
        }
        break;
      case 'observer_joined':
        ctx.dispatch(
          new ObserverJoined(
            payload['game_id'] as string,
            payload['observer'] as { username: string; is_bot: boolean },
          )
        );
        break;
      case 'observer_left':
        ctx.dispatch(
          new ObserverLeft(
            payload['game_id'] as string,
            payload['username'] as string,
          )
        );
        break;
      case 'observation_ended':
        this.router.navigate(['/lobby']);
        break;
      case 'game_chat':
        this.store.dispatch(
          new GameChatReceived(
            payload['game_id'] as string,
            payload['message_id'] as string,
            payload['username'] as string,
            payload['message'] as string,
            payload['timestamp'] as string,
            payload['is_observer'] as boolean,
          )
        );
        break;
      case 'draw_offered':
        // metadata flows through GameStateUpdate; no separate state mutation needed
        break;
      case 'draw_offer_accepted':
        // acceptance ends the game via game_over; no separate state mutation needed
        break;
      case 'draw_offer_declined':
        ctx.dispatch(new DrawOfferDeclinedReceived(payload['game_id'] as string));
        break;
      case 'blocked_users_list':
        ctx.dispatch(new BlockedUsersListReceived(payload['users'] as string[]));
        break;
      case 'stored_games_list':
        ctx.dispatch(new StoredGamesListReceived(payload['games'] as unknown as StoredGameInfo[]));
        break;
      case 'friends_list':
        ctx.dispatch(new FriendsListReceived(payload['friends'] as FriendInfo[]));
        break;
      case 'friend_requests_list':
        ctx.dispatch(new FriendRequestsListReceived(
          payload['incoming'] as RequestInfo[],
          payload['outgoing'] as RequestInfo[],
        ));
        break;
      case 'friend_request_received':
        ctx.dispatch(new FriendRequestReceivedNotification(payload['from_username'] as string));
        break;
      case 'friend_request_sent':
        ctx.dispatch(new FriendRequestSentNotification(payload['target_username'] as string));
        break;
      case 'friend_request_accepted':
        ctx.dispatch(new FriendRequestAcceptedNotification(payload['username'] as string));
        break;
      case 'friend_request_declined':
        ctx.dispatch(new FriendRequestDeclinedNotification(payload['username'] as string));
        break;
      case 'friend_removed':
        ctx.dispatch(new FriendRemovedNotification(payload['username'] as string));
        break;
      case 'friend_online':
        ctx.dispatch(new FriendOnlineNotification(payload['username'] as string));
        break;
      case 'friend_offline':
        ctx.dispatch(new FriendOfflineNotification(payload['username'] as string));
        break;
      case 'profile': {
        const sp = payload['stats'] as Record<string, unknown> | undefined;
        const perVariant: VariantStats[] = Array.isArray(sp?.['per_variant'])
          ? (sp['per_variant'] as unknown[]).map((v) => ({
              game: (v as Record<string, unknown>)['game'] as string,
              games_played: (v as Record<string, unknown>)['games_played'] as number,
              wins: (v as Record<string, unknown>)['wins'] as number,
              losses: (v as Record<string, unknown>)['losses'] as number,
              draws: (v as Record<string, unknown>)['draws'] as number,
            }))
          : [];
        ctx.dispatch(new ProfileReceived({
          username: payload['username'] as string,
          display_name: (payload['display_name'] as string) ?? null,
          avatar_url: (payload['avatar_url'] as string) ?? null,
          online: payload['online'] as boolean,
          friendship: payload['friendship'] as string,
          blocked_by_me: payload['blocked_by_me'] as boolean,
          blocked_me: payload['blocked_me'] as boolean,
          stats: sp ? {
            games_played: sp['games_played'] as number,
            wins: sp['wins'] as number,
            losses: sp['losses'] as number,
            draws: sp['draws'] as number,
            member_since: sp['member_since'] as string,
            per_variant: perVariant,
          } : null,
          bio: (payload['bio'] as string) ?? null,
          notify_friend_online: (payload['notify_friend_online'] as boolean) ?? null,
        }));
        break;
      }
      case 'error':
        ctx.patchState({ profileLoading: false });
        ctx.dispatch(
          new LobbyChatReceived(
            crypto.randomUUID(),
            'System',
            payload['message'] as string,
            'system',
            new Date().toISOString(),
          )
        );
        break;
    }
  }

  @Action(GuestLoginSuccess)
  onLoginSuccess(ctx: StateContext<LobbyStateModel>, action: GuestLoginSuccess) {
    ctx.patchState({
      currentUser: { clientId: action.clientId, username: action.username },
      pendingLogin: null,
    });
    this.router.navigate(['/lobby']);
  }

  @Action(PlayerJoined)
  onPlayerJoined(ctx: StateContext<LobbyStateModel>, action: PlayerJoined) {
    const state = ctx.getState();
    if (!state.players.some((p) => p.username === action.player.username)) {
      ctx.patchState({ players: [...state.players, action.player] });
    }
  }

  @Action(PlayerLeft)
  onPlayerLeft(ctx: StateContext<LobbyStateModel>, action: PlayerLeft) {
    const state = ctx.getState();
    ctx.patchState({
      players: state.players.filter((p) => p.username !== action.username),
    });
  }

  @Action(PlayerListUpdated)
  onPlayerList(ctx: StateContext<LobbyStateModel>, action: PlayerListUpdated) {
    ctx.patchState({ players: action.players });
  }

  @Action(LobbyChatReceived)
  onChat(ctx: StateContext<LobbyStateModel>, action: LobbyChatReceived) {
    const state = ctx.getState();
    ctx.patchState({
      chatMessages: [
        ...state.chatMessages,
        {
          messageId: action.messageId,
          username: action.username,
          message: action.message,
          kind: action.kind,
          timestamp: action.timestamp,
          likes: 0,
        },
      ],
    });
  }

  @Action(KeycloakLogin)
  keycloakLogin() {
    this.authService.login();
  }

  @Action(KeycloakRegister)
  keycloakRegister() {
    this.authService.register();
  }

  @Action(AuthLogin)
  authLogin(ctx: StateContext<LobbyStateModel>, action: AuthLogin) {
    ctx.patchState({ pendingAuthToken: action.token, pendingLogin: null });
    ctx.dispatch(new ConnectWebSocket());
  }

  @Action(AuthLoginSuccess)
  onAuthLoginSuccess(ctx: StateContext<LobbyStateModel>, action: AuthLoginSuccess) {
    ctx.patchState({
      currentUser: { clientId: action.clientId, username: action.username, displayName: action.displayName },
      pendingLogin: null,
      pendingAuthToken: null,
      isGuest: false,
    });
    ctx.dispatch(new ListBlockedUsers());
    ctx.dispatch(new ListFriends());
    ctx.dispatch(new ListFriendRequests());
    this.router.navigate(['/lobby']);
  }

  @Action(ChatHistoryLoaded)
  onChatHistory(ctx: StateContext<LobbyStateModel>, action: ChatHistoryLoaded) {
    const messages: ChatMessage[] = action.messages.map((m) => ({
      messageId: m.message_id,
      username: m.username,
      message: m.message,
      timestamp: m.timestamp,
      kind: m.kind,
      likes: m.likes?.length ?? 0,
    }));
    ctx.patchState({ chatMessages: messages });
  }

  @Action(SendLobbyChat)
  sendChat(ctx: StateContext<LobbyStateModel>, action: SendLobbyChat) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'lobby_chat',
        payload: { message: action.message },
      })
    );
  }

  @Action(AvailableGamesReceived)
  onAvailableGames(ctx: StateContext<LobbyStateModel>, action: AvailableGamesReceived) {
    ctx.patchState({ availableGames: action.games });
  }

  @Action(SendChallenge)
  sendChallenge(ctx: StateContext<LobbyStateModel>, action: SendChallenge) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'send_challenge',
        payload: {
          target_username: action.targetUsername,
          game: action.game,
          initial_time_secs: action.initialTimeSecs,
          increment_secs: action.incrementSecs,
          rated: action.rated,
          public: action.isPublic,
          desired_color: action.desiredColor ?? null,
        },
      })
    );
  }

  @Action(ChallengeSentConfirmed)
  onChallengeSent(ctx: StateContext<LobbyStateModel>, action: ChallengeSentConfirmed) {
    const state = ctx.getState();
    ctx.patchState({
      outgoingChallenges: [
        ...state.outgoingChallenges,
        {
          challengeId: action.challengeId,
          toUsername: action.toUsername,
          game: action.game,
          initialTimeSecs: action.initialTimeSecs,
          incrementSecs: action.incrementSecs,
          rated: action.rated,
          isPublic: action.isPublic,
        },
      ],
    });
  }

  @Action(ChallengeReceived)
  onChallengeReceived(ctx: StateContext<LobbyStateModel>, action: ChallengeReceived) {
    const state = ctx.getState();
    ctx.patchState({
      incomingChallenges: [
        ...state.incomingChallenges,
        {
          challengeId: action.challengeId,
          fromUsername: action.fromUsername,
          game: action.game,
          initialTimeSecs: action.initialTimeSecs,
          incrementSecs: action.incrementSecs,
          rated: action.rated,
          isPublic: action.isPublic,
        },
      ],
    });
  }

  @Action(AcceptChallenge)
  acceptChallenge(ctx: StateContext<LobbyStateModel>, action: AcceptChallenge) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'accept_challenge',
        payload: { challenge_id: action.challengeId },
      })
    );
  }

  @Action(DeclineChallenge)
  declineChallenge(ctx: StateContext<LobbyStateModel>, action: DeclineChallenge) {
    const state = ctx.getState();
    ctx.patchState({
      incomingChallenges: state.incomingChallenges.filter((c) => c.challengeId !== action.challengeId),
    });
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'decline_challenge',
        payload: { challenge_id: action.challengeId },
      })
    );
  }

  @Action(RevokeChallenge)
  revokeChallenge(ctx: StateContext<LobbyStateModel>, action: RevokeChallenge) {
    const state = ctx.getState();
    ctx.patchState({
      outgoingChallenges: state.outgoingChallenges.filter((c) => c.challengeId !== action.challengeId),
    });
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'revoke_challenge',
        payload: { challenge_id: action.challengeId },
      })
    );
  }

  @Action(ChallengeDeclined)
  onChallengeDeclined(ctx: StateContext<LobbyStateModel>, action: ChallengeDeclined) {
    const state = ctx.getState();
    ctx.patchState({
      outgoingChallenges: state.outgoingChallenges.filter((c) => c.challengeId !== action.challengeId),
    });
  }

  @Action(ChallengeRevoked)
  onChallengeRevoked(ctx: StateContext<LobbyStateModel>, action: ChallengeRevoked) {
    const state = ctx.getState();
    ctx.patchState({
      incomingChallenges: state.incomingChallenges.filter((c) => c.challengeId !== action.challengeId),
      outgoingChallenges: state.outgoingChallenges.filter((c) => c.challengeId !== action.challengeId),
    });
  }

  @Action(ChallengeAccepted)
  onChallengeAccepted(ctx: StateContext<LobbyStateModel>) {
    ctx.patchState({
      incomingChallenges: [],
      outgoingChallenges: [],
    });
  }

  @Action(MessageLiked)
  onMessageLiked(ctx: StateContext<LobbyStateModel>, action: MessageLiked) {
    const state = ctx.getState();
    ctx.patchState({
      chatMessages: state.chatMessages.map((m) =>
        m.messageId === action.messageId ? { ...m, likes: action.likeCount } : m
      ),
    });
  }

  @Action(LikeMessage)
  likeMessage(ctx: StateContext<LobbyStateModel>, action: LikeMessage) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'like_message',
        payload: { message_id: action.messageId },
      })
    );
  }

  @Action(ActiveGamesListReceived)
  onActiveGamesList(ctx: StateContext<LobbyStateModel>, action: ActiveGamesListReceived) {
    ctx.patchState({
      activeGames: action.games.map((g) => ({
        gameId: g.game_id,
        game: g.game,
        players: this.normalizePlayerSeats(g.players),
        initialTimeSecs: g.initial_time_secs,
        incrementSecs: g.increment_secs,
        startedAt: g.started_at,
        public: g.public,
        observerCount: g.observer_count,
      })),
    });
  }

  @Action(ActiveGameAdded)
  onActiveGameAdded(ctx: StateContext<LobbyStateModel>, action: ActiveGameAdded) {
    const state = ctx.getState();
    const entry: ActiveGameEntry = {
      gameId: action.game.game_id,
      game: action.game.game,
      players: this.normalizePlayerSeats(action.game.players),
      initialTimeSecs: action.game.initial_time_secs,
      incrementSecs: action.game.increment_secs,
      startedAt: action.game.started_at,
      public: action.game.public,
      observerCount: action.game.observer_count,
    };
    ctx.patchState({
      activeGames: [entry, ...state.activeGames],
    });
  }

  @Action(ActiveGameRemoved)
  onActiveGameRemoved(ctx: StateContext<LobbyStateModel>, action: ActiveGameRemoved) {
    const state = ctx.getState();
    ctx.patchState({
      activeGames: state.activeGames.filter((g) => g.gameId !== action.gameId),
    });
  }

  @Action(ObserveGame)
  observeGame(ctx: StateContext<LobbyStateModel>, action: ObserveGame) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'observe_game',
        payload: { game_id: action.gameId },
      })
    );
  }

  @Action(ObserveStoredGame)
  observeStoredGame(ctx: StateContext<LobbyStateModel>, action: ObserveStoredGame) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'observe_stored_game',
        payload: { game_id: action.gameId },
      })
    );
  }

  @Action(StopObserving)
  stopObserving(ctx: StateContext<LobbyStateModel>, action: StopObserving) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'stop_observing',
        payload: { game_id: action.gameId },
      })
    );
  }

  @Action(SendGameChat)
  sendGameChat(ctx: StateContext<LobbyStateModel>, action: SendGameChat) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'game_chat',
        payload: { game_id: action.gameId, message: action.message },
      })
    );
  }

  @Action(BlockUser)
  blockUser(ctx: StateContext<LobbyStateModel>, action: BlockUser) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'block_user',
        payload: { username: action.username },
      })
    );
  }

  @Action(UnblockUser)
  unblockUser(ctx: StateContext<LobbyStateModel>, action: UnblockUser) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'unblock_user',
        payload: { username: action.username },
      })
    );
  }

  @Action(ListBlockedUsers)
  listBlockedUsers(ctx: StateContext<LobbyStateModel>) {
    ctx.dispatch(new SendWebSocketMessage({ type: 'list_blocked_users', payload: {} }));
  }

  @Action(BlockedUsersListReceived)
  onBlockedUsersList(ctx: StateContext<LobbyStateModel>, action: BlockedUsersListReceived) {
    const state = ctx.getState();
    ctx.patchState({ blockedUsernames: action.users });
    if (state.profile) {
      ctx.dispatch(new GetProfile(state.profile.username));
    }
  }

  @Action(StartDemonstration)
  startDemonstration(ctx: StateContext<LobbyStateModel>, action: StartDemonstration) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'start_demonstration',
        payload: { game: action.game, title: action.title ?? null },
      }),
    );
  }

  @Action(StartReview)
  startReview(ctx: StateContext<LobbyStateModel>, action: StartReview) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'start_review',
        payload: { game_id: action.gameId },
      }),
    );
  }

  @Action(DemonstrationStarted)
  onDemonstrationStarted(ctx: StateContext<LobbyStateModel>, action: DemonstrationStarted) {
    ctx.dispatch(
      new GameStarted(
        action.gameId,
        action.game,
        action.players,
        null,
        null,
        0,
        0,
        'demonstration',
        false,
      ),
    );
  }

  @Action(ListStoredGames)
  listStoredGames(ctx: StateContext<LobbyStateModel>, action: ListStoredGames) {
    ctx.patchState({ storedGamesOffset: action.offset });
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'list_stored_games',
        payload: { mode: action.mode, limit: action.limit, offset: action.offset },
      }),
    );
  }

  @Action(StoredGamesListReceived)
  onStoredGamesList(ctx: StateContext<LobbyStateModel>, action: StoredGamesListReceived) {
    const { storedGamesOffset } = ctx.getState();
    const combined = storedGamesOffset > 0
      ? [...ctx.getState().storedGames, ...action.games]
      : action.games;
    ctx.patchState({ storedGames: combined });
  }

  @Action(FriendsListReceived)
  onFriendsList(ctx: StateContext<LobbyStateModel>, action: FriendsListReceived) {
    ctx.patchState({ friends: action.friends });
  }

  @Action(FriendRequestsListReceived)
  onFriendRequestsList(ctx: StateContext<LobbyStateModel>, action: FriendRequestsListReceived) {
    ctx.patchState({
      incomingFriendRequests: action.incoming,
      outgoingFriendRequests: action.outgoing,
    });
  }

  @Action(FriendRequestReceivedNotification)
  onFriendRequestReceived(ctx: StateContext<LobbyStateModel>, action: FriendRequestReceivedNotification) {
    const state = ctx.getState();
    if (!state.incomingFriendRequests.some(r => r.username === action.fromUsername)) {
      ctx.patchState({
        incomingFriendRequests: [...state.incomingFriendRequests, { username: action.fromUsername }],
      });
    }
  }

  @Action(FriendRequestSentNotification)
  onFriendRequestSent(ctx: StateContext<LobbyStateModel>, action: FriendRequestSentNotification) {
    const state = ctx.getState();
    if (!state.outgoingFriendRequests.some(r => r.username === action.targetUsername)) {
      ctx.patchState({
        outgoingFriendRequests: [...state.outgoingFriendRequests, { username: action.targetUsername }],
      });
    }
    if (state.profile?.username === action.targetUsername) {
      ctx.dispatch(new GetProfile(action.targetUsername));
    }
  }

  @Action(FriendRequestAcceptedNotification)
  onFriendRequestAccepted(ctx: StateContext<LobbyStateModel>, action: FriendRequestAcceptedNotification) {
    const state = ctx.getState();
    const username = action.username;
    const incoming = state.incomingFriendRequests.filter(r => r.username !== username);
    const outgoing = state.outgoingFriendRequests.filter(r => r.username !== username);
    const friends = state.friends.some(f => f.username === username)
      ? state.friends
      : [...state.friends, { username, avatar_url: null, online: false }];
    ctx.patchState({ incomingFriendRequests: incoming, outgoingFriendRequests: outgoing, friends });
    if (state.profile?.username === username) {
      ctx.dispatch(new GetProfile(username));
    }
    ctx.dispatch(new ListFriends());
  }

  @Action(FriendRequestDeclinedNotification)
  onFriendRequestDeclined(ctx: StateContext<LobbyStateModel>, action: FriendRequestDeclinedNotification) {
    const state = ctx.getState();
    ctx.patchState({
      incomingFriendRequests: state.incomingFriendRequests.filter(r => r.username !== action.username),
      outgoingFriendRequests: state.outgoingFriendRequests.filter(r => r.username !== action.username),
    });
  }

  @Action(FriendRemovedNotification)
  onFriendRemoved(ctx: StateContext<LobbyStateModel>, action: FriendRemovedNotification) {
    const state = ctx.getState();
    ctx.patchState({
      friends: state.friends.filter(f => f.username !== action.username),
    });
    if (state.profile?.username === action.username) {
      ctx.dispatch(new GetProfile(action.username));
    }
  }

  @Action(FriendOnlineNotification)
  onFriendOnline(ctx: StateContext<LobbyStateModel>, action: FriendOnlineNotification) {
    const state = ctx.getState();
    const username = action.username;

    const friends = state.friends.map(f =>
      f.username === username ? { ...f, online: true } : f
    );
    ctx.patchState({ friends });

    if (!state.notifyFriendOnline) return;

    const activeGame = this.store.selectSnapshot(GameState.activeGame);
    if (activeGame && activeGame.result === null) return;

    if (state.friendOnlineBuffer.includes(username)) return;
    const buffer = [...state.friendOnlineBuffer, username];
    ctx.patchState({ friendOnlineBuffer: buffer });

    if (this.friendOnlineTimer) {
      clearTimeout(this.friendOnlineTimer);
    }
    this.friendOnlineTimer = setTimeout(() => {
      const current = [...new Set(ctx.getState().friendOnlineBuffer)];
      if (current.length === 0) return;
      ctx.patchState({ friendOnlineBuffer: [] });
      this.zone.run(() => {
        if (current.length === 1) {
          this.snackBar.open(
            this.translate.instant('FRIENDS.NOW_ONLINE', { username: current[0] }),
            undefined,
            { duration: 3000 },
          );
        } else {
          this.snackBar.open(
            this.translate.instant('FRIENDS.MANY_ONLINE', { count: current.length }),
            undefined,
            { duration: 3000 },
          );
        }
      });
    }, 2000);
  }

  @Action(FriendOfflineNotification)
  onFriendOffline(ctx: StateContext<LobbyStateModel>, action: FriendOfflineNotification) {
    const state = ctx.getState();
    ctx.patchState({
      friends: state.friends.map(f =>
        f.username === action.username ? { ...f, online: false } : f
      ),
    });
  }

  @Action(SendFriendRequest)
  sendFriendRequest(ctx: StateContext<LobbyStateModel>, action: SendFriendRequest) {
    ctx.dispatch(new SendWebSocketMessage({
      type: 'send_friend_request',
      payload: { username: action.username },
    }));
  }

  @Action(AcceptFriendRequest)
  acceptFriendRequest(ctx: StateContext<LobbyStateModel>, action: AcceptFriendRequest) {
    ctx.dispatch(new SendWebSocketMessage({
      type: 'accept_friend_request',
      payload: { username: action.username },
    }));
  }

  @Action(DeclineFriendRequest)
  declineFriendRequest(ctx: StateContext<LobbyStateModel>, action: DeclineFriendRequest) {
    ctx.dispatch(new SendWebSocketMessage({
      type: 'decline_friend_request',
      payload: { username: action.username },
    }));
  }

  @Action(CancelFriendRequest)
  cancelFriendRequest(ctx: StateContext<LobbyStateModel>, action: CancelFriendRequest) {
    ctx.dispatch(new SendWebSocketMessage({
      type: 'cancel_friend_request',
      payload: { username: action.username },
    }));
  }

  @Action(RemoveFriend)
  removeFriend(ctx: StateContext<LobbyStateModel>, action: RemoveFriend) {
    ctx.dispatch(new SendWebSocketMessage({
      type: 'remove_friend',
      payload: { username: action.username },
    }));
  }

  @Action(ListFriends)
  listFriends(ctx: StateContext<LobbyStateModel>) {
    ctx.dispatch(new SendWebSocketMessage({ type: 'list_friends', payload: {} }));
  }

  @Action(ListFriendRequests)
  listFriendRequests(ctx: StateContext<LobbyStateModel>) {
    ctx.dispatch(new SendWebSocketMessage({ type: 'list_friend_requests', payload: {} }));
  }

  @Action(GetProfile)
  getProfile(ctx: StateContext<LobbyStateModel>, action: GetProfile) {
    ctx.patchState({ profile: null, profileLoading: true });
    ctx.dispatch(new SendWebSocketMessage({
      type: 'get_profile',
      payload: { username: action.username },
    }));
  }

  @Action(ProfileReceived)
  onProfileReceived(ctx: StateContext<LobbyStateModel>, action: ProfileReceived) {
    ctx.patchState({ profile: action.profile, profileLoading: false });
  }

  @Action(ClearProfile)
  onClearProfile(ctx: StateContext<LobbyStateModel>) {
    ctx.patchState({ profile: null, profileLoading: false });
  }

  @Action(UpdateProfile)
  updateProfile(_ctx: StateContext<LobbyStateModel>, action: UpdateProfile) {
    const payload: Record<string, unknown> = { target_username: action.target_username };
    if (action.display_name !== null) payload['display_name'] = action.display_name;
    if (action.bio !== null) payload['bio'] = action.bio;
    if (action.avatar_url !== null) payload['avatar_url'] = action.avatar_url;
    if (action.notify_friend_online !== null) payload['notify_friend_online'] = action.notify_friend_online;
    _ctx.dispatch(new SendWebSocketMessage({ type: 'update_profile', payload }));
  }

  @Action(SetPlayerListTab)
  onSetPlayerListTab(ctx: StateContext<LobbyStateModel>, action: SetPlayerListTab) {
    ctx.patchState({ playerListTab: action.tab });
  }

  @Action(UpdateNotifyFriendOnline)
  updateNotifyFriendOnline(ctx: StateContext<LobbyStateModel>, action: UpdateNotifyFriendOnline) {
    ctx.patchState({ notifyFriendOnline: action.enabled });
    ctx.dispatch(new SendWebSocketMessage({
      type: 'update_notify_friend_online',
      payload: { enabled: action.enabled },
    }));
  }
}
